import React, { useCallback, useEffect, useRef, useState } from "react"
import anime from "animejs"
import classNames from "classnames"
import dayjs from "dayjs"
import { clamp } from "lodash-es"
import { usePresence } from "motion/react"

import { useETL, useTranslation } from "@/locales"
import Toast from "@/topic/rogue_5/components/Toast"
import { gameComplete } from "@/topic/rogue_5/service"
import { normalRandomFast } from "@/topic/rogue_5/utils/misc"
import { SFX } from "@/topic/rogue_5/utils/sfx"
// import { getRoguelikeItem } from "@/utils/assets"
import { needFixWritingMode } from "@/utils/misc"
import MainButton from "../../components/MainButton"
import { useEffectModal } from "../../components/Modal/EffectModal"
import { useResultModal } from "../../components/Modal/ResultModal"
import { BackButton } from "../../components/RightButton"
import { EGameScene, useGameStore } from "../../store"

import tablePng from "../../../../assets/img/game/game/table.png"
import tableWebP from "../../../../assets/img/game/game/table.webp"
import styles from "./index.module.less"

const basic_front_in_animation = {
    opacity: [0, 1],
    duration: 2000,
    easing: "easeOutCubic",
}

const SCENE_ANIMATION = {
    animate: async (element: HTMLDivElement) => {
        if (!element) {
            return Promise.resolve()
        }
        const fenceLeft = element.querySelector(
            `.${styles.fence}.${styles.left}`
        )
        const fenceRight = element.querySelector(
            `.${styles.fence}.${styles.right}`
        )
        const flags = element.querySelectorAll(`.${styles.flag}`)
        const table = element.querySelector(`.${styles.table}`)
        // const shadow = element.querySelector(`.${styles.shadow}`)
        const timeline = anime.timeline()
        timeline.add(
            {
                targets: fenceLeft,
                ...basic_front_in_animation,
                filter: ["blur(10px)", "blur(0px)"],
                scale: [0.9, 1],
                translateX: ["10rem", "0rem"],
                translateY: ["-8rem", "-4rem"],
            },
            0
        )
        timeline.add(
            {
                targets: fenceRight,
                ...basic_front_in_animation,
                filter: ["blur(10px)", "blur(0px)"],
                translateX: ["-10rem", "0rem"],
                translateY: ["-8rem", "-4rem"],
                scale: [0.9, 1],
            },
            0
        )
        flags.forEach((flag, index) => {
            timeline.add(
                {
                    targets: flag,
                    duration: 400,
                    begin: () => {
                        flag.classList.add(styles.active)
                        if (index === 0) {
                            SFX.play("gameIn")
                        }
                    },
                },
                300 * index
            )
        })
        timeline.add(
            {
                targets: element.querySelector(`.${styles.flags}`),
                duration: 2000,
                scale: [0.9, 1],
                easing: "easeOutCubic",
            },
            1500
        )
        timeline.add(
            {
                targets: fenceLeft,
                filter: ["blur(0px)", "blur(5px)"],
                translateX: [0, "-2rem"],
                translateY: ["-4rem", 0],
                duration: 2000,
                easing: "easeOutCubic",
            },
            1500
        )
        timeline.add(
            {
                targets: fenceRight,
                filter: ["blur(0px)", "blur(5px)"],
                translateX: [0, "2rem"],
                translateY: ["-4rem", 0],
                duration: 2000,
                easing: "easeOutCubic",
            },
            1500
        )
        timeline.add(
            {
                targets: table,
                filter: ["blur(10px)", "blur(0px)"],
                opacity: [0, 1],
                scale: [0.8, 1],
                duration: 2000,
                easing: "easeOutCubic",
            },
            1500
        )

        // timeline.add(
        //     {
        //
        //     },
        //     5500
        // )
        // timeline.finished.then(() => {
        //     anime({
        //         targets: shadow,
        //         filter: ["blur(10px)", "blur(0px)"],
        //         opacity: [0, 0.7],
        //         scale: [0.8, 1],
        //         duration: 2000,
        //         easing: "easeOutCubic",
        //     })
        // })
        return timeline.finished
    },
    exit: async (element: HTMLDivElement) => {
        if (!element) {
            return Promise.resolve()
        }
        const fenceLeft = element.querySelector(
            `.${styles.fence}.${styles.left}`
        )
        const fenceRight = element.querySelector(
            `.${styles.fence}.${styles.right}`
        )
        const table = element.querySelector(`.${styles.table}`)
        const flags = element.querySelector(`.${styles.flags}`)
        const results = element.querySelector(`.${styles.results}`)
        const timeline = anime.timeline()
        timeline.add(
            {
                targets: [table, flags, results],
                filter: ["blur(0px)", "blur(5px)"],
                opacity: [1, 0],
                scale: [1, 1.05],
                duration: 1000,
                easing: "easeOutCubic",
            },
            0
        )
        timeline.add(
            {
                targets: [fenceLeft, fenceRight],
                opacity: 0,
                duration: 500,
                easing: "easeOutCubic",
            },
            0
        )
        return timeline.finished
    },
}

const getRandomPosition = () => {
    const x = clamp(normalRandomFast(0, 0.7), -1, 1) * 200
    const y = clamp(normalRandomFast(0, 0.7), -1, 1) * 200
    if ((x + 158) * (x + 158) + (y + 244) * (y + 244) < 160 * 160) {
        return getRandomPosition()
    }
    return {
        x,
        y,
    }
}
const getCoinsPosition = () => {
    const ret: { x: number; y: number }[] = []

    for (let i = 0; i < 3; i++) {
        while (true) {
            const pos = getRandomPosition()
            if (
                ret.some(
                    (p) =>
                        Math.abs(p.x - pos.x) < 40 && Math.abs(p.y - pos.y) < 40
                )
            ) {
                continue
            }
            ret.push(pos)
            break
        }
    }

    return ret.map(({ x, y }) => {
        return {
            translateX: `${(x / 16).toFixed(2)}rem`,
            translateY: `${(y / 16).toFixed(2)}rem`,
        }
    })
}
const callCoinAnimation = (ele: HTMLDivElement) => {
    if (!ele) {
        return Promise.resolve()
    }
    const drawer = ele.querySelector(`.${styles.drawer}`)
    const coinEles = ele.querySelectorAll(
        `.${styles.coin}`
    ) as NodeListOf<HTMLImageElement>
    // const resultEles = ele.querySelectorAll(
    //     `.${styles.result}`
    // ) as NodeListOf<HTMLDivElement>
    const timeline = anime.timeline()
    // resultEles.forEach((ele) => {
    //     timeline.add(
    //         {
    //             targets: ele,
    //             opacity: 0,
    //             duration: 500,
    //             easing: "easeOutCubic",
    //         },
    //         0
    //     )
    // })
    SFX.play("coinIn")
    timeline.add(
        {
            targets: drawer,
            translateY: ["100%", "0%"],
            duration: 800,
            easing: "cubicBezier(0.1, 0.5, 0.0, 1.0)",
        },
        0
    )
    const positions = getCoinsPosition()
    coinEles.forEach((coin, index) => {
        // const x = (clamp(normalRandomFast(0, 0.5), -1, 1) * 200) / 16
        // const y = (clamp(normalRandomFast(0, 0.5), -1, 1) * 200) / 16

        // setInterval(() => {
        //     const { translateX, translateY } = getRandomPosition()
        //     coin.style.transform = `translate(${translateX}, ${translateY})`
        // }, 500)
        const { translateX, translateY } = positions[index]
        const deg = (Math.random() * 360).toFixed(2)

        timeline.add(
            {
                targets: coin,
                scale: [1.1, 1],
                translateX: [translateX, translateX],
                translateY: [translateY, translateY],
                rotate: [`${deg}deg`, `${deg}deg`],
                duration: 1000,
                easing: "easeOutBounce",
            },
            500 + 300 * index
        )
        timeline.add(
            {
                targets: coin,
                opacity: [0, 1],

                duration: 300,
                easing: "easeOutCubic",
            },
            500 + 300 * index
        )
    })
    // resultEles.forEach((ele, index) => {
    //     timeline.add(
    //         {
    //             targets: ele,
    //             opacity: [0, 1],
    //             translateY: ["40%", "0%"],
    //             duration: 1000,
    //             easing: "easeOutCubic",
    //         },
    //         1600 + 300 * index
    //     )
    // })
    return timeline.finished
}
const showResult = (ele: HTMLDivElement) => {
    if (!ele) {
        return Promise.resolve()
    }
    const resultEles = ele.querySelectorAll(
        `.${styles.result}`
    ) as NodeListOf<HTMLDivElement>
    const timeline = anime.timeline()
    SFX.play("flagIn")
    resultEles.forEach((ele, index) => {
        timeline.add(
            {
                targets: ele,
                opacity: [0, 1],
                translateY: ["-30%", "0%"],
                duration: 1000,
                easing: "easeOutCubic",
            },
            300 * index
        )
    })
    return timeline.finished
}
const hideResult = (ele: HTMLDivElement) => {
    if (!ele) {
        return Promise.resolve()
    }
    const resultEles = ele.querySelectorAll(
        `.${styles.result}`
    ) as NodeListOf<HTMLDivElement>
    const coinEles = ele.querySelectorAll(
        `.${styles.coin}`
    ) as NodeListOf<HTMLImageElement>
    resultEles.forEach((ele) => {
        anime({
            targets: ele,
            opacity: 0,
            translateY: ["0%", "40%"],
            duration: 500,
            easing: "easeOutCubic",
        })
    })
    coinEles.forEach((ele) => {
        anime({
            targets: ele,
            opacity: 0,
            duration: 500,
            easing: "easeOutCubic",
        })
    })
}

const ResultFlag: React.FC<{
    className?: string
    style?: React.CSSProperties
    coin: string
    title?: string
    content?: string
    needReverse?: boolean
}> = ({ className, style, coin, title, content, needReverse }) => {
    const { t } = useTranslation()
    return (
        <div
            className={classNames(
                styles.result,
                needFixWritingMode() && styles.safari,
                className
            )}
            style={style}
        >
            <div
                className={classNames(
                    styles.frame,
                    !coin && styles.resultFrame
                )}
            >
                {coin && <img className={styles.innerCoin} src={coin} />}
            </div>
            <div
                className={classNames(
                    styles.title,

                    !coin && styles.resultTitle
                )}
            >
                <span className={styles.text}>
                    {title?.split("").map((text) => (
                        <span className={styles.titleChar} key={text}>
                            {text}
                        </span>
                    ))}
                </span>
            </div>
            <div className={styles.divider}></div>
            <div
                className={classNames(
                    styles.content,
                    styles.newContent,
                    needReverse ? "" : styles.leftContent,
                    !coin && styles.resultContent
                )}
            >
                {content}
            </div>
        </div>
    )
}

const GameSceneGame: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const { setScene, gameResult, rowGameResult, gameType, restarted } =
        useGameStore()
    // animation
    const [isPresent, safeToRemove] = usePresence()
    const containerRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    useEffect(() => {
        if (!containerRef.current) return
        if (isPresent) {
            SCENE_ANIMATION.animate(containerRef.current).then(() => {
                if (gameResult) {
                    Promise.all([
                        callCoinAnimation(containerRef.current!),
                        showResult(containerRef.current!),
                    ]).then(() => {
                        setShowButton(true)
                    })
                } else {
                    setShowButton(true)
                }
            })
        } else {
            setShowButton(false)
            SCENE_ANIMATION.exit(containerRef.current).then(() => {
                safeToRemove()
            })
        }
    }, [isPresent])

    // row animation
    const [showButton, setShowButton] = useState(false)

    const callEffectModal = useEffectModal()
    const callResultModal = useResultModal()
    const onRow = useCallback(() => {
        // 重抽时，先隐藏结果
        hideResult(containerRef.current!)
        setShowButton(false)
        anime({
            targets: containerRef.current?.querySelectorAll(`.${styles.fence}`),
            opacity: 0,
            duration: 500,
            easing: "easeOutCubic",
        })
        callEffectModal(async () => {
            const result = rowGameResult()
            const special = result.result.special
            // await sleep(100)
            callResultModal(
                {
                    special,
                    title: special
                        ? t("game.modal.title.special")
                        : t("game.modal.title.normal"),
                    titleEn: special
                        ? t("game.modal.title.special.en")
                        : t("game.modal.title.normal.en"),
                    content: result.result.description,
                },
                () => {
                    callCoinAnimation(containerRef.current!).then(() => {
                        showResult(containerRef.current!).then(() => {
                            setShowButton(true)
                        })
                    })
                }
            )
        })
    }, [callEffectModal, callResultModal, rowGameResult, t])
    const onConfirm = useCallback(() => {
        const now = dayjs().unix()
        if (now < __ROGUE_5_LOTTERY_END_AT) {
            gameComplete().then((res) => {
                if (res.code === 0) {
                    useGameStore.setState((state) => ({
                        info: {
                            ...state.info,
                            game: {
                                complete: true,
                            },
                        },
                    }))
                    setScene(EGameScene.Result)
                } else {
                    Toast.message({ content: res.msg })
                }
            })
        } else {
            setScene(EGameScene.Result)
        }
    }, [setScene])
    const collect = useETL()
    const [gameStartTs, setGameStartTs] = useState(0)
    return (
        <div
            className={classNames(styles.sceneContainer, className)}
            style={style}
            ref={containerRef}
        >
            <div className={styles.flags}>
                <div
                    className={classNames(
                        styles.flag,
                        gameType === "rhodes" && styles.rhodes
                    )}
                ></div>
                <div
                    className={classNames(
                        styles.flag,
                        gameType === "rhodes" && styles.rhodes
                    )}
                ></div>
                <div
                    className={classNames(
                        styles.flag,
                        gameType === "rhodes" && styles.rhodes
                    )}
                ></div>
                <div
                    className={classNames(
                        styles.flag,
                        gameType === "rhodes" && styles.rhodes
                    )}
                ></div>
            </div>
            <div className={styles.table}>
                <div className={styles.drawer}></div>
                <div className={styles.bg}>
                    <picture>
                        <source srcSet={tableWebP} type="image/webp" />
                        <img src={tablePng} alt="table" />
                    </picture>
                </div>
                <div className={styles.poster}></div>
                <div className={styles.label}></div>
                <div className={styles.cup}></div>
                <div className={styles.book}></div>
                <div className={styles.coins}>
                    {gameResult?.coins.map((c) => (
                        <img
                            key={c.id}
                            className={styles.coin}
                            src={`${__ASSETS_HOST}assets/img/static/rogue_5/copper/${c.id}.png`}
                        />
                    ))}
                </div>
            </div>
            <div className={styles.results}>
                <ResultFlag
                    coin=""
                    title={t("game.game.result.title")}
                    needReverse={false}
                    content={`${gameResult?.coins.map((c) => c.name).join(t("comma"))}${t("period")}\n${gameResult?.result.description}`}
                />
                {gameResult?.coins.map((c) => (
                    <ResultFlag
                        key={c.id}
                        coin={`${__ASSETS_HOST}assets/img/static/rogue_5/copper/${c.id}.png`}
                        title={c.name}
                        needReverse={true}
                        content={[
                            c.desc.slice(0, 20),
                            c.desc.slice(20, 40),
                            c.desc.slice(40),
                        ].join("\n")}
                    />
                ))}
            </div>
            <div className={classNames(styles.fence, styles.left)}></div>
            <div className={classNames(styles.fence, styles.right)}></div>
            {/* <div className={styles.shadow}></div> */}
            <div
                className={classNames(
                    styles.buttonGroup,
                    showButton && styles.active
                )}
            >
                {!gameResult ? (
                    <MainButton
                        type="yellow"
                        mark="wen"
                        icon={gameType === "rhodes" ? "rhodes" : "man"}
                        className={styles.button}
                        text={t("game.button.row.cn")}
                        textEn={t("game.button.row.en")}
                        onClick={() => {
                            onRow()
                            const startTs = Date.now()
                            setGameStartTs(startTs)
                            collect?.("mini_game_start", {
                                game_type: gameType,
                                is_restart: false,
                                game_start_ts: startTs,
                            })
                        }}
                    />
                ) : (
                    <>
                        <MainButton
                            type="orange"
                            mark="wen"
                            icon={gameType === "rhodes" ? "rhodes" : "man"}
                            className={styles.button}
                            text={t("game.button.rowAgain.cn")}
                            textEn={t("game.button.rowAgain.en")}
                            onClick={() => {
                                onRow()
                                collect?.("mini_game_start", {
                                    game_type: gameType,
                                    is_restart: true,
                                    game_start_ts: gameStartTs,
                                })
                            }}
                        />
                        <MainButton
                            type="purple"
                            mark="jie"
                            icon="flower"
                            className={styles.button}
                            text={t("game.button.confirm.cn")}
                            textEn={t("game.button.confirm.en")}
                            onClick={() => {
                                onConfirm()
                                collect?.("mini_game_end", {
                                    game_type: gameType,
                                    game_result: gameResult.result.id,
                                })
                            }}
                        />
                    </>
                )}
            </div>
            <BackButton
                className={classNames(
                    styles.backButton,
                    !showButton && styles.hidden
                )}
                onClick={() => {
                    setScene(EGameScene.Lobby)
                }}
            />
        </div>
    )
}

export default GameSceneGame
